import React, { useMemo, useRef, useState } from 'react';
import { AutoComplete, Avatar, Input, Spin, Tag, Typography } from 'antd';
import {
  SearchOutlined,
  LoadingOutlined,
  UserOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useGetSearchResultsQuery } from '@/store/apis/coreApi';
import { debounce } from '@/utils/debound.utils';
import { useNavigate } from 'react-router-dom';

type AssessmentType = 'purchase' | 'refinance';

interface Assessment {
  id: number;
  status: string;
  user: {
    first_name: string;
    last_name: string;
    fullname: string;
    email: string;
  };
  google_sheet_url: string;
}

interface User {
  fullname: string;
  email: string;
  first_name: string;
  last_name: string;
  id: number;
}

const STATUS_COLOR: Record<string, string> = {
  APPROVED: 'green',
  PENDING: 'orange',
  SUBMITTED: 'blue',
  DECLINED: 'red',
  IN_PROGRESS: 'processing',
};

const TYPE_COLOR: Record<AssessmentType, string> = {
  purchase: 'green',
  refinance: 'blue',
};

function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatStatus(status: string) {
  return (status ?? '')
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(' ');
}

const CategoryLabel: React.FC<{
  label: string;
  color: string;
  count: number;
}> = ({ label, color, count }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '4px 0',
    }}
  >
    <Typography.Text
      style={{
        fontWeight: 700,
        fontSize: 11,
        color: '#8c8c8c',
        letterSpacing: '0.08em',
      }}
    >
      {label}
    </Typography.Text>
    <Tag color={color} style={{ fontSize: 11, lineHeight: '16px', margin: 0 }}>
      {count}
    </Tag>
  </div>
);

const ResultItem: React.FC<{
  avatar: React.ReactNode;
  title: React.ReactNode;
  subtitle: string;
  badge?: React.ReactNode;
}> = ({ avatar, title, subtitle, badge }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '2px 0',
    }}
  >
    {avatar}
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
        }}
      >
        <Typography.Text strong style={{ fontSize: 13 }}>
          {title}
        </Typography.Text>
        {badge}
      </div>
      <Typography.Text
        type="secondary"
        style={{ fontSize: 11, display: 'block' }}
        ellipsis
      >
        {subtitle}
      </Typography.Text>
    </div>
  </div>
);

const SearchBar: React.FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const debouncedSetQuery = useMemo(
    () => debounce((value: string) => setDebouncedQuery(value), 500),
    [],
  );

  const skip = !debouncedQuery || debouncedQuery.length < 3;

  const { data, isLoading, isFetching } = useGetSearchResultsQuery(
    { query: debouncedQuery },
    {
      skip: skip,
      refetchOnMountOrArgChange: true,
    },
  );

  const results = useMemo(() => {
    if (!debouncedQuery || debouncedQuery.length < 3) return null;
    return data?.data || null;
  }, [data, debouncedQuery]);

  const navigate = useNavigate();

  const options = useMemo(() => {
    if (isFetching || isLoading) return [];
    if (!results || !debouncedQuery) return [];

    const result: {
      label: React.ReactNode;
      options: {
        key: string;
        value: string;
        label: React.ReactNode;
        [k: string]: unknown;
      }[];
    }[] = [];

    function buildCategory(
      label: string,
      color: string,
      items: {
        key: string;
        value: string;
        label: React.ReactNode;
        [k: string]: unknown;
      }[],
    ) {
      return {
        label: (
          <CategoryLabel label={label} color={color} count={items.length} />
        ),
        options: items,
      };
    }

    // Assessments (purchase + refinance merged into one group)
    const assessments: { type: AssessmentType; app: Assessment }[] = [
      ...((results.purchase as Assessment[] | undefined) ?? []).map((app) => ({
        type: 'purchase' as const,
        app,
      })),
      ...((results.refinance as Assessment[] | undefined) ?? []).map((app) => ({
        type: 'refinance' as const,
        app,
      })),
    ];

    if (assessments.length) {
      result.push(
        buildCategory(
          'ASSESSMENTS',
          'green',
          assessments.map(({ type, app }, i) => {
            const name =
              app.user.fullname ||
              `${app.user.first_name} ${app.user.last_name}`;
            const statusKey = (app.status ?? '').toUpperCase();
            return {
              key: `assessment-${type}-${app.id}-${i}`,
              value: `${name} (${app.user.email}) : ${type.toUpperCase()} Assessment ${app.id}`,
              app,
              type,
              label: (
                <ResultItem
                  avatar={
                    <Avatar
                      size={32}
                      style={{
                        background: type === 'purchase' ? '#f6ffed' : '#e6f4ff',
                        color: type === 'purchase' ? '#52c41a' : '#1677ff',
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(name)}
                    </Avatar>
                  }
                  title={name}
                  subtitle={app.user.email}
                  badge={
                    <span style={{ display: 'flex', gap: 4 }}>
                      <Tag
                        color={TYPE_COLOR[type]}
                        style={{
                          fontSize: 10,
                          lineHeight: '16px',
                          margin: 0,
                        }}
                      >
                        {type === 'purchase' ? 'Purchase' : 'Refinance'}
                      </Tag>
                      {statusKey && (
                        <Tag
                          color={STATUS_COLOR[statusKey] ?? 'default'}
                          style={{
                            fontSize: 10,
                            lineHeight: '16px',
                            margin: 0,
                          }}
                        >
                          {formatStatus(app.status)}
                        </Tag>
                      )}
                    </span>
                  }
                />
              ),
            };
          }),
        ),
      );
    }

    if (results.users?.length) {
      const users = results.users as User[];
      result.push(
        buildCategory(
          'USERS',
          'purple',
          users.map((user, i) => ({
            key: `user-${user.id}-${i}`,
            value: `${user.first_name} ${user.last_name} (${user.email})`,
            user,
            label: (
              <ResultItem
                avatar={
                  <Avatar
                    size={32}
                    icon={<UserOutlined />}
                    style={{
                      background: '#f9f0ff',
                      color: '#722ed1',
                      flexShrink: 0,
                    }}
                  />
                }
                title={`${user.first_name} ${user.last_name}`}
                subtitle={user.email}
              />
            ),
          })),
        ),
      );
    }

    return result;
  }, [results, debouncedQuery, isFetching, isLoading]);

  const onSelect = (_value: string, option: Record<string, unknown>) => {
    const app = option.app as Assessment | undefined;
    const type = option.type as AssessmentType | undefined;
    const user = option.user as User | undefined;

    if (app && type) {
      navigate(`/client/details/${type}/${app.id}/assessment-detail`, {
        replace: true,
      });
    } else if (user) {
      navigate(`/users/${user.email}`);
    }

    setInputValue('');
    setDebouncedQuery('');
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const autoCompleteRef = useRef<any>(null);

  const anyFetching = isFetching || isLoading;
  const showLoading = anyFetching && inputValue.length >= 3;
  const showNoResults =
    !anyFetching && debouncedQuery.length >= 3 && options.length === 0;

  return (
    <AutoComplete
      ref={autoCompleteRef}
      style={{ width: '100%', margin: '1rem 0' }}
      options={options}
      value={inputValue}
      onSelect={onSelect}
      onChange={(value) => {
        setInputValue(value);
        const trimmed = value.trim();
        if (trimmed.length >= 3) {
          debouncedSetQuery(trimmed);
        } else {
          setDebouncedQuery('');
        }
      }}
      popupRender={(menu) => (
        <div>
          {showLoading ? (
            <div
              style={{
                padding: '24px 16px',
                textAlign: 'center',
                color: '#8c8c8c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              <Spin indicator={<LoadingOutlined spin />} size="small" />
              <Typography.Text type="secondary">
                Searching for "{inputValue}"…
              </Typography.Text>
            </div>
          ) : showNoResults ? (
            <div
              style={{
                padding: '24px 16px',
                textAlign: 'center',
              }}
            >
              <Typography.Text type="secondary">
                No results found for{' '}
                <Typography.Text strong>"{debouncedQuery}"</Typography.Text>
              </Typography.Text>
            </div>
          ) : (
            menu
          )}

          {debouncedQuery.length >= 3 && !showLoading && (
            <div
              role="presentation"
              style={{
                borderTop: '1px solid #f0f0f0',
                padding: '10px 16px',
                background: '#fafafa',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                color: '#1677ff',
                fontWeight: 500,
                fontSize: 13,
                transition: 'background 0.2s',
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const query = debouncedQuery;
                autoCompleteRef.current?.blur();
                setInputValue('');
                setDebouncedQuery('');
                navigate(`/contacts/search/results/?searchQuery=${query}`);
              }}
            >
              <span>See all results for "{debouncedQuery}"</span>
              <ArrowRightOutlined style={{ fontSize: 12 }} />
            </div>
          )}
        </div>
      )}
    >
      <Input
        size="large"
        allowClear
        placeholder="Search assessments or users by name, email, or ID..."
        prefix={
          showLoading ? (
            <LoadingOutlined style={{ color: '#1677ff' }} spin />
          ) : (
            <SearchOutlined style={{ color: '#bfbfbf' }} />
          )
        }
      />
    </AutoComplete>
  );
};

export default SearchBar;
